import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import React, { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateCommission } from 'hooks/queries/useCreateCommission';
import { usePermissions } from 'hooks/usePermissions';
import { CommissionsFormValues } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/components/MerchantShowCommissionsForm/MerchantShowCommissionsForm';
import { CommissionCreateTargetStatus } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/constants/commissionCreateTargetStatus';
import { COMMISSIONS_SCHEMA } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/schemas/schema';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { Form, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Create } from 'shared/react-admin/Create/Create';
import { Commission } from 'types/embedded/commission';

import { MerchantShowCommissionsCreateDialogForm } from './MerchantShowCommissionsCreateDialogForm';
import { transform } from '../utils/transform';

export type CommissionCreateResult = {
  data: Commission;
  targetStatus: CommissionCreateTargetStatus;
  withRefundAndChargeback: boolean;
};

type Props = {
  open: boolean;
  merchantId: string;
  onClose: () => void;
  onSuccess: (result: CommissionCreateResult) => void;
};

type FormValues = CommissionsFormValues & {
  createRelatedPaymentCommissions: boolean;
};

export const MerchantShowCommissionsCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  merchantId,
}) => {
  const translate = useTypedTranslate();
  const { roles } = usePermissions();
  const [errors, setErrors] = useState<string[]>([]);

  const targetStatus = useRef<CommissionCreateTargetStatus>(CommissionCreateTargetStatus.Approved);

  const notify = useNotify();

  const { createCommissionAsync } = useCreateCommission();

  const onSubmit = async (data: FormValues) => {
    const transformedData = transform(data);

    try {
      const result = await createCommissionAsync({
        data: {
          ...transformedData,
          merchant: merchantId,
        },
        targetStatus: targetStatus.current,
      });

      queryClient.invalidateQueries({ queryKey: [Resources.Commissions] });
      onClose();
      onSuccess({
        data: result,
        targetStatus: targetStatus.current,
        withRefundAndChargeback: data.createRelatedPaymentCommissions,
      });
    } catch (error) {
      setErrors((error as Error)?.message?.split('\n'));
      notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
        type: 'error',
      });
    }
  };

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open} width="1200px">
      <Create resource="commissions" sx={{ '.RaCreate-noActions': { margin: 0 } }}>
        <Form
          defaultValues={{
            merchant: merchantId,
            createRelatedPaymentCommissions: false,
          }}
          onSubmit={onSubmit as any}
          resolver={yupResolver<FieldValues>(COMMISSIONS_SCHEMA)}
        >
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.merchantCommissions.newFeeCreation')}
            titleSize="smallBold"
          />
          <MerchantShowCommissionsCreateDialogForm errors={errors} merchantId={merchantId} />
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel" onClick={onClose} variant="outlined" />,
              <SaveButton
                key="create"
                label={translate('app.buttons.create')}
                onClick={() => (targetStatus.current = CommissionCreateTargetStatus.Created)}
                variant="contained"
              />,
              roles?.includes(Role.Admin) && (
                <SaveButton
                  key="create-and-approve"
                  label={translate('app.merchantCommissions.createAndApprove')}
                  onClick={() => (targetStatus.current = CommissionCreateTargetStatus.Approved)}
                  variant="contained"
                />
              ),
            ]}
          />
        </Form>
      </Create>
    </Dialog>
  );
};
